import { PanelLeft } from 'lucide-react';
import { AnimatePresence, motion } from 'motion/react';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';

import { Card } from '@core/components/Card';
import { cn } from '@core/utils/styles';

import { Button } from '../Button';
import { Text } from '../Text';

const PATH_TITLES = [
  { path: '/settings', titleKey: 'settings.title' },
  { path: '/dashboard', titleKey: 'dashboard.title' },
  { path: '/crm', titleKey: 'crm.title' },
  { path: '/messaging', titleKey: 'messaging.title' },
];

interface SidebarLayoutProps extends React.PropsWithChildren {
  sidebar: React.ReactNode;
  expanded: boolean;
  setExpanded: (expanded: boolean) => void;
}

export function SidebarLayout({ sidebar, expanded, setExpanded, children }: SidebarLayoutProps) {
  const location = useLocation();
  const { t } = useTranslation();

  const titleKey = PATH_TITLES.find(({ path }) => location.pathname.includes(path))?.titleKey || '';
  const title = titleKey ? t(titleKey) : '';

  return (
    <div className="relative isolate flex h-screen min-h-svh w-full py-2 md:gap-2 md:pr-2">
      <div className="relative h-full">{sidebar}</div>

      <AnimatePresence>
        {expanded && (
          <motion.div
            className="md-border md:rounded-theme fixed inset-0 z-6 flex-1 bg-black/30 md:hidden dark:bg-black/50"
            onClick={() => setExpanded(false)}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          />
        )}
      </AnimatePresence>

      <Card
        variant="panel"
        className="md-border md:rounded-theme z-5 h-full flex-1 overflow-auto rounded-none border-0 md:overflow-hidden"
      >
        <div className="mx-auto flex h-auto min-h-0 max-w-7xl flex-1 flex-col md:h-full">
          <div className="border-stroke-light dark:border-stroke-dark flex items-center gap-4 border-b p-4 2xl:px-0">
            <Button
              onClick={() => setExpanded(!expanded)}
              size="icon-sm"
              variant="ghost"
              className="md:hidden"
            >
              <PanelLeft
                className={cn('text-txt-light dark:text-txt-dark size-5 transition-all')}
              />
            </Button>

            <Text as="h1" size="xl" weight="medium" className="truncate">
              {title}
            </Text>
          </div>

          <div className="h-auto min-h-0 flex-1 flex-col p-4 md:h-full 2xl:px-0">{children}</div>
        </div>
      </Card>
    </div>
  );
}
